import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Braces,
  Clock,
  FileCode,
  BookOpen,
  Info,
  Mail,
  Shield,
  Cookie,
  FileText,
  Sun,
  Moon,
  Monitor,
  Github
} from 'lucide-react';
import { useLanguage } from '../../i18n';

/**
 * Dashboard 侧边栏组件
 * 复刻Dashboard设计风格
 */
function Sidebar({ theme, setTheme }) {
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();

  // 工具菜单
  const toolsMenu = [
    {
      id: 'json',
      path: '/json',
      icon: Braces,
      name: { zh: 'JSON 格式化', en: 'JSON Formatter' },
      color: 'text-blue-600 dark:text-blue-400'
    },
    {
      id: 'timestamp',
      path: '/timestamp',
      icon: Clock,
      name: { zh: '时间戳转换', en: 'Timestamp Converter' },
      color: 'text-orange-600 dark:text-orange-400'
    },
    {
      id: 'gostruct',
      path: '/gostruct',
      icon: FileCode,
      name: { zh: 'Go ↔ JSON', en: 'Go ↔ JSON' },
      color: 'text-emerald-600 dark:text-emerald-400'
    }
  ];

  // 内容菜单
  const contentMenu = [
    {
      path: '/blog',
      icon: BookOpen,
      name: { zh: '博客', en: 'Blog' }
    }
  ];

  // 页面菜单
  const pagesMenu = [
    {
      path: '/about',
      icon: Info,
      name: { zh: '关于我们', en: 'About' }
    },
    {
      path: '/contact',
      icon: Mail,
      name: { zh: '联系我们', en: 'Contact' }
    },
    {
      path: '/privacy',
      icon: Shield,
      name: { zh: '隐私政策', en: 'Privacy' }
    },
    {
      path: '/cookie-policy',
      icon: Cookie,
      name: { zh: 'Cookie政策', en: 'Cookies' }
    },
    {
      path: '/terms',
      icon: FileText,
      name: { zh: '服务条款', en: 'Terms' }
    }
  ];

  const isActive = (path) => location.pathname === path;
  const isToolActive = (path) => location.pathname.startsWith(path);

  return (
    <aside className="w-64 h-screen sticky top-0 flex flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
      {/* Logo */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <Link to="/" className="flex items-center gap-3">
          <img src="/logo.svg" alt="DevToolkit" className="w-10 h-10 rounded-xl shadow-lg" />
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">DevToolkit</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('nav.brand')}</p>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* 工具分组 */}
        <div>
          <h3 className="px-3 mb-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            {language === 'zh' ? '工具' : 'Tools'}
          </h3>
          <div className="space-y-1">
            {toolsMenu.map((item) => {
              const Icon = item.icon;
              const active = isToolActive(item.path);

              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                    active
                      ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-medium shadow-sm'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <Icon size={18} className={active ? item.color : ''} />
                  <span className="text-sm">{item.name[language]}</span>
                </Link>
              );
            })}
          </div>
        </div>

        {/* 内容分组 */}
        <div>
          <h3 className="px-3 mb-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            {language === 'zh' ? '内容' : 'Content'}
          </h3>
          <div className="space-y-1">
            {contentMenu.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path) || location.pathname.startsWith(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                    active
                      ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-medium shadow-sm'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <Icon size={18} />
                  <span className="text-sm">{item.name[language]}</span>
                </Link>
              );
            })}
          </div>
        </div>

        {/* 页面分组 */}
        <div>
          <h3 className="px-3 mb-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            {language === 'zh' ? '页面' : 'Pages'}
          </h3>
          <div className="space-y-1">
            {pagesMenu.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                    active
                      ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 font-medium shadow-sm'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <Icon size={18} />
                  <span className="text-sm">{item.name[language]}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* 底部工具区 */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
        {/* 主题切换 */}
        <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-2 flex items-center justify-center gap-1">
          <button
            onClick={() => setTheme('light')}
            className={`p-2 rounded-md transition-all duration-200 ${
              theme === 'light'
                ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
            title={t('nav.lightMode')}
          >
            <Sun size={16} />
          </button>
          <button
            onClick={() => setTheme('dark')}
            className={`p-2 rounded-md transition-all duration-200 ${
              theme === 'dark'
                ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
            title={t('nav.darkMode')}
          >
            <Moon size={16} />
          </button>
          <button
            onClick={() => setTheme('system')}
            className={`p-2 rounded-md transition-all duration-200 ${
              theme === 'system'
                ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
            title={t('nav.systemMode')}
          >
            <Monitor size={16} />
          </button>
        </div>

        {/* 语言切换 */}
        <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-2 flex items-center justify-center gap-2">
          <button
            onClick={() => setLanguage('zh')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
              language === 'zh'
                ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            中文
          </button>
          <button
            onClick={() => setLanguage('en')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
              language === 'en'
                ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            EN
          </button>
        </div>

        {/* GitHub 链接 */}
        <a
          href="https://github.com/RongShiMr/JsonPreview"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 text-white rounded-lg transition-colors text-sm font-medium"
        >
          <Github size={16} />
          <span>GitHub</span>
        </a>
      </div>
    </aside>
  );
}

export default Sidebar;
